import React, { useCallback, useEffect, useState } from 'react';
import { Button, CustomSelect, CustomSelectItem, styleConstants, useFeatureFlag } from '@hwm/ui-lib';
import { t, Trans } from '@lingui/macro';
import {
  FeatureFlagsEnum,
  FilterType,
  SearchTypeOptionsFn,
  SortColumnOptionsFn,
  SortOrderOptionsFn,
} from '@shared/models';
import { FilterList } from '@material-ui/icons';
import { SiteListMenu } from '../SiteListMenu/SiteListMenu';
import { isEmpty, isNil } from 'lodash';
import { Collapse, makeStyles, Theme } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import { useSiteListState } from '../useSiteListFiltersState';
import { useAppSelector } from '@core/store/store';
import { userMainAccStateSelector } from '@core/store/library-stores/selectors';
import { useSidebar } from '@shared/hooks';
import { preferencesSelector } from '@core/store/entity/preferences/PreferencesSelector';

const useStyles = makeStyles((theme: Theme) => ({
  select: {
    height: 32,
    maxHeight: 32,
    zIndex: 200,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    '& .select__control': {
      minWidth: 60,
    },
  },
  selectMarginRight: {
    marginRight: theme.spacing(2),
  },
  typeSearchSelect: {
    height: 40,
    maxHeight: 40,
    zIndex: 200,
    width: 152,
    maxWidth: 152,

    '&:first-child': {
      marginRight: theme.spacing(2),
    },
    '& .select__control': {
      minWidth: 40,
    },

    [theme.breakpoints.down('xs')]: {
      width: 140,
      maxWidth: 140,
    },
  },
  searchInputContainer: {
    height: '40px',
    position: 'relative',
    flex: '1',
    '& :focus': {
      outline: 'none',
    },
    '& input': {
      border: `2.5px solid ${styleConstants.colors.primaryColor}`,
      borderRadius: '5px',
      color: '#596379',
      height: '40px',
      paddingLeft: '34px',
      paddingRight: '34px',
      position: 'absolute',
      top: '0px',
      width: '100%',
      zIndex: 1,
      '& :focus': {
        outline: 'none',
      },
      '&::-ms-clear': {
        display: 'none',
      },
    },
  },
  searchIcon: {
    position: 'absolute',
    top: '9px',
    left: '10px',
    zIndex: 10,
    width: '20px',
  },
  clearIcon: {
    cursor: 'pointer',
    position: 'absolute',
    top: '9px',
    right: '10px',
    zIndex: 10,
    width: '20px',
  },
}));

interface SiteListFiltersProps {
  loading: boolean;
  handleDefaultState: () => void;
}

export const SiteListFilters: React.FC<SiteListFiltersProps> = ({ loading, handleDefaultState }) => {
  const classes = useStyles();
  const { hasFeatureFlag } = useFeatureFlag();

  const [showFilters, setShowFilters] = useState(true);
  const [searchState, setSearchState] = useState('');

  const {
    sidebar: { state: sidebarState },
  } = useSidebar();

  const {
    siteListState: { search, searchType, filter, sortOrder, columnSort, zone, zoneOptions, preferencesValuesSet },
    setSearchFilter,
    setSearchFilterType,
    setSiteFilter,
    setSortOrder,
    setColumnSort,
    setZoneFilter,
    setPreferencesValuesSet,
  } = useSiteListState();

  const { useZones } = useAppSelector(userMainAccStateSelector);
  const { preferences: preferencesValues } = useAppSelector(preferencesSelector);

  const canUseAllSearch = hasFeatureFlag(FeatureFlagsEnum.AllowAllSearchField);

  const clearSearchVal = useCallback(() => {
    setSearchState('');
    setSearchFilter('');
  }, [setSearchFilter]);

  const performSearch = useCallback(() => {
    if (search !== searchState) {
      setSearchFilter(searchState);
    }
  }, [search, searchState, setSearchFilter]);

  const handleSearchValChange = (event: React.FormEvent<HTMLInputElement>) => {
    setSearchState(event.currentTarget.value);
  };

  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      performSearch();
    }
  };

  useEffect(() => {
    if (!sidebarState) {
      clearSearchVal();
    }
  }, [sidebarState, clearSearchVal]);

  useEffect(() => {
    if (preferencesValuesSet || isNil(preferencesValues)) {
      return;
    }
    preferencesValues?.siteListSortOrder && setSortOrder(preferencesValues.siteListSortOrder);
    preferencesValues?.siteListColumnSort && setColumnSort(preferencesValues.siteListColumnSort);
    preferencesValues?.siteListSearchType && setSearchFilterType(preferencesValues.siteListSearchType);
    setPreferencesValuesSet(true);
  }, [
    preferencesValuesSet,
    preferencesValues,
    setSortOrder,
    setColumnSort,
    setSearchFilterType,
    setPreferencesValuesSet,
  ]);

  return (
    <div className="flex flex-col mb-2">
      <div className="flex flex-row justify-between space-x-1 h-8 mb-2">
        <Button
          className="w-1/2"
          style={{ height: 32 }}
          variant={filter === FilterType.All ? 'contained' : 'text'}
          onClick={() => {
            if (filter === FilterType.All) return;

            setSiteFilter(FilterType.All);
            setSearchFilter('');
            setSearchState('');
          }}
        >
          <Trans>All</Trans>
        </Button>
        <Button
          className="w-1/2"
          style={{ height: 32 }}
          variant={filter === FilterType.Selected ? 'contained' : 'text'}
          onClick={() => {
            if (filter === FilterType.Selected) return;

            setSiteFilter(FilterType.Selected);
            setSearchFilter('');
            setSearchState('');
          }}
        >
          <Trans>Selected</Trans>
        </Button>
        <SiteListMenu handleDefaultState={handleDefaultState} />
        <Button style={{ height: 32, minWidth: 48, width: 48 }} onClick={() => setShowFilters((p) => !p)}>
          <FilterList />
        </Button>
      </div>
      <Collapse in={showFilters}>
        {useZones ? (
          <div className="mr-0.5 mb-2">
            <CustomSelect
              label={t`Zone`}
              placeholder={t`Select`}
              onChange={(selectedValue) => setZoneFilter((selectedValue as CustomSelectItem)?.value ?? '')}
              options={zoneOptions && !isEmpty(zoneOptions) ? zoneOptions.map((el) => ({ label: el, value: el })) : []}
              className={classes.select}
              minWidth={148}
              value={zone ? { label: zone === 'all' ? t`All` : zone, value: zone } : null}
              isDisabled={false}
              isError={false}
              clearable
              newEditableLook
            />
          </div>
        ) : null}
        <div className="flex mr-0.5 mb-2">
          <CustomSelect
            label={t`Sort by`}
            placeholder="Please Select"
            onChange={(selectedValue) => setColumnSort((selectedValue as CustomSelectItem).value)}
            options={Object.entries(SortColumnOptionsFn()).map(([value, label]) => ({
              label,
              value,
            }))}
            minWidth={148}
            className={`${classes.select} ${classes.selectMarginRight}`}
            value={{
              label: SortColumnOptionsFn()[columnSort],
              value: columnSort,
            }}
            isDisabled={false}
            isError={false}
            newEditableLook
          />

          <CustomSelect
            label={t`Order`}
            placeholder="Please Select"
            onChange={(selectedValue) => setSortOrder((selectedValue as CustomSelectItem).value)}
            options={Object.entries(SortOrderOptionsFn()).map(([value, label]) => ({
              label,
              value,
            }))}
            minWidth={144}
            className={classes.select}
            value={{
              label: SortOrderOptionsFn()[sortOrder],
              value: sortOrder,
            }}
            isDisabled={false}
            isError={false}
            newEditableLook
          />
        </div>
        <div className="flex flex-row items-start mr-0.5">
          <CustomSelect
            label={t`Type`}
            placeholder="Please Select"
            onChange={(selectedValue) => setSearchFilterType((selectedValue as CustomSelectItem).value)}
            options={Object.entries(SearchTypeOptionsFn(canUseAllSearch)).map(([value, label]) => ({
              label,
              value,
            }))}
            minWidth={100}
            className={classes.typeSearchSelect}
            value={{
              label: SearchTypeOptionsFn(canUseAllSearch)[searchType],
              value: searchType,
            }}
            isDisabled={false}
            isError={false}
            newEditableLook
          />
          <div className={classes.searchInputContainer}>
            <input
              placeholder={t`Search`}
              type="text"
              value={searchState}
              onChange={handleSearchValChange}
              onKeyUp={handleEnter}
            />
            <SearchIcon className={classes.searchIcon} />
            {!!searchState.length && <ClearIcon onClick={clearSearchVal} className={classes.clearIcon} />}
          </div>
          <Button
            onClick={performSearch}
            style={{
              marginLeft: 8,
              marginTop: 2,
              marginBottom: 2,
              height: 36,
              width: 60,
              outline: 'none',
            }}
            disabled={loading}
          >
            <Trans>Search</Trans>
          </Button>
        </div>
      </Collapse>
    </div>
  );
};
